import React, { useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../api";

export default function TransferenciaLeadModal({
  isOpen,
  onClose,
  lead,
  usuarios,
  onTransferido,
}) {
  const [novoUsuarioId, setNovoUsuarioId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (isOpen && lead) {
      setNovoUsuarioId("");
      setMotivo("");
      setObservacao("");
    }
  }, [isOpen, lead]);

  const limparTexto = (texto) =>
    String(texto || "")
      .replace(/\s+/g, " ")
      .trim();

  const contarPalavras = (texto) =>
    limparTexto(texto).split(" ").filter(Boolean).length;

  const validarObservacao = (texto) => {
    const textoLimpo = limparTexto(texto);
    const quantidadePalavras = contarPalavras(textoLimpo);

    if (!textoLimpo) {
      return {
        ok: false,
        mensagem: "A observação é obrigatória.",
      };
    }

    if (textoLimpo.length < 30) {
      return {
        ok: false,
        mensagem: "A observação deve ter no mínimo 30 caracteres.",
      };
    }

    if (quantidadePalavras < 5) {
      return {
        ok: false,
        mensagem: "A observação deve ter no mínimo 5 palavras.",
      };
    }

    return {
      ok: true,
      textoLimpo,
      quantidadePalavras,
    };
  };

  const isLeadBloqueada = (leadAtual) => {
    const valor = leadAtual?.cadeado;
    return valor === true || valor === 1 || valor === "1";
  };

  const usuariosUnicos = useMemo(() => {
    const mapa = new Map();

    for (const u of usuarios || []) {
      if (!mapa.has(String(u.id))) {
        mapa.set(String(u.id), u);
      }
    }

    return Array.from(mapa.values());
  }, [usuarios]);

  const usuariosDestino = useMemo(() => {
    if (!lead) return [];

    const cargosPermitidos = [
      "vendedor",
      "administrador",
      "admin",
      "comercial",
      "negociador",
      "marketing",
      "supervisor",
      "recepcionista",
    ];

    return usuariosUnicos.filter((u) => {
      const naoEhAtual = String(u.id) !== String(lead.usuario_criador_id);
      const estaAtivo = Number(u.ativo) === 1 || u.ativo === true;
      const cargo = String(u.cargo || "")
        .trim()
        .toLowerCase();
      const podeReceberLead = cargosPermitidos.includes(cargo);

      return naoEhAtual && estaAtivo && podeReceberLead;
    });
  }, [usuariosUnicos, lead]);

  const nomeCaptadorAtual = useMemo(() => {
    if (!lead) return "";

    if (!lead.usuario_criador_id) {
      return "Sem captador vinculado";
    }

    const usuarioAtual = usuariosUnicos.find(
      (u) => String(u.id) === String(lead.usuario_criador_id),
    );

    if (usuarioAtual?.nome) {
      return usuarioAtual.nome;
    }

    return `Captador atual ID ${lead.usuario_criador_id} (não carregado na lista)`;
  }, [usuariosUnicos, lead]);

  if (!isOpen || !lead) return null;

  const leadBloqueada = isLeadBloqueada(lead);
  const observacaoPreview = limparTexto(observacao);
  const totalCaracteres = observacaoPreview.length;
  const totalPalavras = contarPalavras(observacao);
  const observacaoValida = totalCaracteres >= 30 && totalPalavras >= 5;

  const handleSalvar = async () => {
    if (leadBloqueada) {
      toast.warning(
        "Esta lead está bloqueada e não pode ser enviada para aprovação de transferência.",
      );
      return;
    }

    if (!novoUsuarioId) {
      toast.error("Selecione o captador de destino.");
      return;
    }

    if (!motivo) {
      toast.error("Selecione o motivo da solicitação.");
      return;
    }

    const validacaoObservacao = validarObservacao(observacao);
    if (!validacaoObservacao.ok) {
      toast.error(validacaoObservacao.mensagem);
      return;
    }

    try {
      setSalvando(true);

      await api.post(`/leads/${lead.id}/transferir`, {
        novo_usuario_criador_id: Number(novoUsuarioId),
        motivo: limparTexto(motivo),
        observacao: validacaoObservacao.textoLimpo,
      });

      toast.success("Solicitação de transferência enviada para aprovação!");

      if (typeof onTransferido === "function") {
        await onTransferido();
      }

      onClose();
    } catch (err) {
      console.error("❌ Erro ao solicitar transferência:", err);

      const status = err?.response?.status;
      const data = err?.response?.data || {};

      if (status === 423) {
        toast.warning(
          data?.erro ||
            data?.error ||
            data?.message ||
            "Esta lead está bloqueada e não pode ser enviada para aprovação de transferência.",
        );
        return;
      }

      if (status === 409) {
        toast.warning(
          data?.erro ||
            data?.error ||
            data?.message ||
            "Já existe um pedido de transferência para essa lead.",
        );
        return;
      }

      const msg =
        data?.erro ||
        data?.error ||
        data?.message ||
        "Erro ao solicitar transferência da lead.";

      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h2 className="text-xl font-bold text-blue-800 mb-2">
          Solicitar Transferência de Lead
        </h2>

        <p className="text-sm text-slate-600 mb-5">
          A lead não será transferida imediatamente. A solicitação ficará
          pendente até aprovação.
        </p>

        {leadBloqueada && (
          <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <strong>Atenção:</strong> esta lead está bloqueada e não pode ser
            enviada para aprovação de transferência enquanto o cadeado estiver
            ativo.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Lead</label>
            <input
              value={lead.nome_lead || ""}
              disabled
              className="w-full border rounded-lg px-3 py-2 bg-slate-100"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Captador atual
            </label>
            <input
              value={nomeCaptadorAtual}
              disabled
              className="w-full border rounded-lg px-3 py-2 bg-slate-100"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Novo captador
            </label>
            <select
              value={novoUsuarioId}
              onChange={(e) => setNovoUsuarioId(e.target.value)}
              disabled={leadBloqueada || salvando}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500"
            >
              <option value="">Selecione</option>
              {usuariosDestino.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome} {u.cargo ? `- ${u.cargo}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Motivo</label>
            <select
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={leadBloqueada || salvando}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500"
            >
              <option value="">Selecione o motivo</option>
              <option value="Lead cadastrada com captador incorreto">
                Lead cadastrada com captador incorreto
              </option>
              <option value="Lead sem andamento comercial">
                Lead sem andamento comercial
              </option>
              <option value="Transferência do negociador para o comercial">
                Transferência do negociador para o comercial
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Observação
            </label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              rows={4}
              placeholder="Descreva com clareza o contexto da solicitação de transferência"
              disabled={leadBloqueada || salvando}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500"
            />

            <div className="mt-2 flex items-center justify-between text-xs">
              <span
                className={
                  observacaoValida ? "text-green-600" : "text-amber-600"
                }
              >
                {totalCaracteres}/30 caracteres • {totalPalavras}/5 palavras
              </span>

              {!observacaoValida && !leadBloqueada && (
                <span className="text-slate-500">
                  Mínimo: 30 caracteres e 5 palavras
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300"
            type="button"
          >
            Cancelar
          </button>

          <button
            onClick={handleSalvar}
            disabled={salvando || leadBloqueada}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
            type="button"
          >
            {salvando ? "Enviando..." : "Enviar Solicitação"}
          </button>
        </div>
      </div>
    </div>
  );
}
